import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Button, Pill } from "../../components/common/Primitives";
import DistributionSettings from "../../components/DistributionSettings";
import {
  Loader2, Sparkles, Download, Send, RefreshCw, TrendingUp, AlertTriangle,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";

export default function AdminMarketReport() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [distBusy, setDistBusy] = useState(null);
  const [failedByReport, setFailedByReport] = useState({});
  const [resendBusy, setResendBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/market-report/history?limit=12");
      const rows = data.items || [];
      setItems(rows);
      // Fetch failed-recipient counts in parallel — only for distributed reports
      const distributed = rows.filter((r) => r.distributed);
      const results = await Promise.all(
        distributed.map((r) =>
          api.get(`/admin/market-report/${r.report_id}/failed-recipients`)
            .then((res) => [r.report_id, res.data?.count || 0])
            .catch(() => [r.report_id, 0])
        )
      );
      setFailedByReport(Object.fromEntries(results));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/market-report/generate");
      toast.success(`Report ${data.report.report_id} generated (source: ${data.report.narrative_source}).`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const distribute = async (rid) => {
    if (!confirm("Send this Weekly Market Report to all active investors + startups (respecting opt-outs)?")) return;
    setDistBusy(rid);
    try {
      const { data } = await api.post(`/admin/market-report/${rid}/distribute`);
      toast.success(`Sent to ${data.sent}. Errors: ${data.errors}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDistBusy(null); }
  };

  const resendFailed = async (rid) => {
    const count = failedByReport[rid] || 0;
    if (count === 0) { toast.info("No failed / bounced recipients on record."); return; }
    if (!confirm(`Re-send this report to ${count} failed / bounced recipient${count === 1 ? "" : "s"}?`)) return;
    setResendBusy(rid);
    try {
      const { data } = await api.post(`/admin/market-report/${rid}/resend-failed`);
      toast.success(`Re-sent to ${data.sent}. Errors: ${data.errors}. Targeted: ${data.resolved}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResendBusy(null); }
  };

  const downloadPdf = async (rid, weekEnding) => {
    try {
      const res = await api.get(`/admin/market-report/${rid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `venturaz-market-report-${weekEnding?.slice(0, 10) || rid}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell
      title="Weekly Market Report"
      subtitle="ADMIN · MARKET INTELLIGENCE"
      actions={
        <div className="flex gap-2">
          <Button variant="primary" onClick={generate} disabled={busy} data-testid="admin-mr-generate">
            {busy ? <Loader2 className="animate-spin inline mr-1" size={12} /> : <Sparkles size={12} className="inline mr-1" />}
            Generate this week&apos;s report
          </Button>
          <Button variant="ghost" onClick={load} data-testid="admin-mr-refresh"><RefreshCw size={12} className="inline mr-1" /> Refresh</Button>
        </div>
      }
    >
      <Toaster richColors position="top-right" />

      <Card testId="admin-mr-explainer" className="mb-6">
        <div className="overline mb-2 flex items-center gap-2"><TrendingUp size={14} /> How it works</div>
        <div className="text-sm text-ink-soft leading-relaxed">
          Each Monday at 04:00 UTC, VenturaZ snapshots the last 7 days of marketplace activity — new
          startups, investor engagement, sector heat shifts, and top diligence reports — and generates
          an <span className="font-heading text-[#8A6600]">AI-drafted analyst note</span> using Claude Sonnet 4.5.
          You can also generate + distribute reports on-demand from here.
        </div>
      </Card>

      <DistributionSettings />

      {items === null && <div className="text-ink-muted"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading history…</div>}

      {items && items.length === 0 && (
        <Card testId="admin-mr-empty">
          <div className="text-sm text-ink-muted">
            No reports yet. Click &quot;Generate this week&apos;s report&quot; to create the first one.
          </div>
        </Card>
      )}

      {items && items.length > 0 && (
        <div className="grid gap-3">
          {items.map((r) => (
            <Card key={r.report_id} testId={`admin-mr-card-${r.report_id}`} className="p-0 overflow-hidden">
              <div className="p-4 border-b border-navy/[0.06] flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="font-heading text-ink text-lg">Week ending {r.week_ending?.slice(0, 10)}</div>
                    <Pill tone={r.narrative_source === "ai" ? "gold" : "muted"}>{r.narrative_source === "ai" ? "AI" : "Fallback"}</Pill>
                    {r.distributed && <Pill tone="success">Distributed · {r.distributed_count}</Pill>}
                  </div>
                  <div className="text-[11px] font-mono text-ink-muted">
                    {r.report_id} · generated {r.generated_at?.slice(0, 16).replace("T", " ")} · by {r.generated_by}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="ghost" onClick={() => downloadPdf(r.report_id, r.week_ending)} data-testid={`admin-mr-pdf-${r.report_id}`}>
                    <Download size={12} className="inline mr-1" /> PDF
                  </Button>
                  {r.distributed && (failedByReport[r.report_id] || 0) > 0 && (
                    <Button
                      variant="ghost"
                      disabled={resendBusy === r.report_id}
                      onClick={() => resendFailed(r.report_id)}
                      data-testid={`admin-mr-resend-failed-${r.report_id}`}
                      title="Re-send to recipients whose last attempt failed or bounced"
                    >
                      {resendBusy === r.report_id
                        ? <Loader2 className="animate-spin inline mr-1" size={12} />
                        : <AlertTriangle size={12} className="inline mr-1 text-rose-600" />}
                      Resend failed ({failedByReport[r.report_id]})
                    </Button>
                  )}
                  <Button
                    variant={r.distributed ? "ghost" : "primary"}
                    disabled={distBusy === r.report_id}
                    onClick={() => distribute(r.report_id)}
                    data-testid={`admin-mr-distribute-${r.report_id}`}
                  >
                    {distBusy === r.report_id
                      ? <Loader2 className="animate-spin inline mr-1" size={12} />
                      : <Send size={12} className="inline mr-1" />}
                    {r.distributed ? "Resend to all" : "Distribute"}
                  </Button>
                </div>
              </div>
              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs bg-navy/[0.02]">
                <KpiCell label="Startups" val={r.data?.kpis?.new_startups} prev={r.data?.kpis?.new_startups_prev} />
                <KpiCell label="Investors" val={r.data?.kpis?.new_investors} prev={r.data?.kpis?.new_investors_prev} />
                <KpiCell label="Interests" val={r.data?.kpis?.new_interests} prev={r.data?.kpis?.new_interests_prev} />
                <KpiCell label="Deal Rooms" val={r.data?.kpis?.new_deal_rooms} prev={r.data?.kpis?.new_deal_rooms_prev} />
              </div>
              {r.narrative && (
                <div className="p-4 text-xs text-ink-soft leading-relaxed border-t border-navy/[0.06] whitespace-pre-wrap">
                  {r.narrative}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}


function KpiCell({ label, val, prev }) {
  const delta = (val ?? 0) - (prev ?? 0);
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "•";
  const color = delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-ink-muted";
  return (
    <div className="border border-navy/[0.06] rounded px-3 py-2 bg-white">
      <div className="text-[10px] font-heading uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="flex items-baseline justify-between mt-1">
        <span className="font-mono text-ink text-lg">{val ?? 0}</span>
        <span className={`text-[10px] font-mono ${color}`}>{arrow} {delta >= 0 ? "+" : ""}{delta}</span>
      </div>
    </div>
  );
}
